import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import { LoadingBlock } from '@/components/PageHeader';
import { inputStyle } from '@/lib/format';
import { kindOf, useCaseSuggestions } from '@/pages/legacy/caseApi';

/**
 * TBM 「1일 1사고사례」 고르개(DOC-136 · 2026-10-28) — 사례 모음의 사고 · 아차사고에서 고른 작업종류의 공종에 맞는 줄을 앞에 보인다.
 * 줄을 고르고 [넣기] — 특이사항 끝에 「1일 1사고사례: …」 로 붙는다.
 */
export function CaseSuggestPicker({ works, onPick, onClose }: { works: string[]; onPick: (line: string) => void; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const suggestions = useCaseSuggestions(works, q, 8);
  const rows = suggestions.data ?? [];

  // 검색어는 치는 동안 멈춘 뒤에 묻는다.
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(typed), 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const chosen = rows.find((row) => row.key === picked) ?? null;

  return (
    <Modal
      title="사고사례"
      onClose={onClose}
      maxWidth="720px"
      actions={
        <button
          type="button"
          className="btn-primary"
          disabled={!chosen}
          onClick={() => {
            if (!chosen) return;
            onPick(chosen.line);
            onClose();
          }}
        >
          넣기
        </button>
      }
    >
      <div className="space-y-3">
        <style>{STYLES}</style>
        <input className="input" style={{ ...inputStyle, width: '100%' }} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="검색" aria-label="검색" />
        {suggestions.isPending ? (
          <LoadingBlock />
        ) : suggestions.isError ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <div className="ss-empty">사례가 없습니다.</div>
        ) : (
          <ul className="cs-list">
            {rows.map((row) => {
              const kind = kindOf(row.kind);
              return (
                <li key={row.key}>
                  <button type="button" className={picked === row.key ? 'on' : ''} onClick={() => setPicked(row.key)} onDoubleClick={() => {
                      onPick(row.line);
                      onClose();
                    }}
                  >
                    <span className="cs-head">
                      <span className={`badge ${kind.tone}`}>{kind.label}</span>
                      {row.category && <span>{row.category}</span>}
                      {row.trade && <span>{row.trade}</span>}
                      <span className="cs-date">{row.date ?? ''}</span>
                    </span>
                    <span className="cs-line">{row.line}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}

const STYLES = `
.cs-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
.cs-list button { display: grid; gap: 4px; width: 100%; padding: 10px 12px; text-align: left; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-card, #fff); font: inherit; color: inherit; cursor: pointer; }
.cs-list button:hover { border-color: var(--accent); }
.cs-list button.on { border-color: var(--accent); background: var(--accent-bg); }
.cs-head { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; color: var(--text-secondary); }
.cs-date { margin-left: auto; color: var(--text-muted); }
.cs-line { font-size: 13px; word-break: break-word; }
`;
